import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { submitQuoteRequest } from "@/lib/quotes.functions";
import { site } from "@/data/site";
import { services } from "@/data/services";
import { trackConversion } from "@/lib/track";
import {
  InventoryCalculator,
  inventoryLines,
  inventoryVolumeAdvised,
  type InventoryState,
} from "@/components/site/InventoryCalculator";

const homeTypes = [
  "Appartement",
  "Eengezinswoning",
  "Tussenwoning",
  "Hoekwoning",
  "Vrijstaande woning",
  "Studio / kamer",
  "Kantoor / bedrijfsruimte",
  "Opslag",
];

const floorOptions = [
  "Begane grond",
  "1e etage",
  "Zolder",
  "Souterrain / kelder",
];



const liftOptions = ["Ja", "Nee", "Weet ik niet"];

const packingOptions = ["Ja", "Nee, ik pak zelf in", "Graag advies"];

const packingHelp: Record<string, string> = {
  Ja: "Wij leveren al het verhuismateriaal en pakken uw inboedel in. Tijdens de opname schatten wij het aantal dozen en de benodigde m³ in; een volledige woning doen wij meestal in twee dagen, inclusief demontage en montage.",
  "Nee, ik pak zelf in":
    "U pakt zelf in; wij leveren op verzoek het verhuismateriaal. Alle grote meubels beschermen wij standaard met wikkelfolie en verhuisdekens.",
  "Graag advies": "Geen probleem, onze klantenservice bespreekt tijdens de opname wat het beste bij u past.",
};

const montageItems = [
  "Bed / boxspring",
  "Kledingkast",
  "Bank",
  "Eettafel",
  "Bureau",
  "Boekenkast",
  "Wasmachine / droger",
  "Tv-meubel of tv-beugel",
];

function joinAddress(street: FormDataEntryValue | null, number: FormDataEntryValue | null) {
  const s = String(street ?? "").trim().slice(0, 240);
  const n = String(number ?? "").trim().slice(0, 20);
  if (!s && !n) return null;
  return n ? `${s} — huisnr. ${n}` : s;
}

export function QuoteForm({ compact = false, service }: { compact?: boolean; service?: string }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [extras, setExtras] = useState<string[]>([]);
  const [needsLift, setNeedsLift] = useState<string>("");
  const [packing, setPacking] = useState<string>("");
  const [montage, setMontage] = useState<string[]>([]);
  const [floors, setFloors] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  const [chosenService, setChosenService] = useState<string>("");
  const [inventory, setInventory] = useState<InventoryState>({});
  const mountedAt = useRef<number>(Date.now());
  const submitQuote = useServerFn(submitQuoteRequest);


  const toggleMontage = (value: string) =>
    setMontage((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const toggleFloor = (value: string) =>
    setFloors((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));


  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setLoading(true);

    const payload = {
      name: String(data.get("name") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      phone: String(data.get("phone") ?? "").trim(),
      move_date: (String(data.get("move_date") ?? "") || null) as string | null,
      from_address: joinAddress(data.get("from_address"), data.get("from_number")),
      to_address: joinAddress(data.get("to_address"), data.get("to_number")),
      home_type: String(data.get("home_type") ?? "") || null,
      surface: String(data.get("surface") ?? "").trim() || null,
      service: service || chosenService || null,
      message: String(data.get("message") ?? "").trim() || null,
      extras: [
        ...extras,
        ...(packing ? [`Inpakservice: ${packing}`] : []),
        ...montage.map((item) => `Demonteren/monteren: ${item}`),
        ...(floors.length ? [`Verdieping(en): ${floors.join(", ")}`] : []),
        ...(String(data.get("floor_other") ?? "").trim()
          ? [`Verdieping (anders): ${String(data.get("floor_other")).trim().slice(0, 60)}`]
          : []),
      ],
      needs_lift: needsLift || null,
      volume_m3: inventoryVolumeAdvised(inventory) || null,
      inventory: inventoryLines(inventory).length ? inventory : null,

      // Honeypot + tijdcontrole tegen spambots.
      company_website: String(data.get("company_website") ?? ""),
      elapsed_ms: Date.now() - mountedAt.current,
    };

    try {
      await submitQuote({ data: payload });
    } catch {
      setLoading(false);
      toast.error("Verzenden lukte niet. Bel of WhatsApp ons, dan regelen we het direct.");
      return;
    }
    setLoading(false);

    setDone(true);
    setExtras([]);
    setNeedsLift("");
    setPacking("");
    setMontage([]);
    setFloors([]);

    setChosenService("");
    setInventory({});
    form.reset();
    trackConversion("offerte_verzonden", { dienst: form["service"] });
    toast.success("Bedankt! We nemen zo snel mogelijk contact met je op.");
  }

  if (done) {
    return (
      <div className="rounded-xl border border-border bg-background p-8 text-center">
        <CheckCircle2 className="mx-auto size-10 text-brand" aria-hidden />
        <h2 className="mt-4 text-2xl font-extrabold text-secondary">Aanvraag ontvangen</h2>
        <p className="mt-2 text-muted-foreground">
          Wij bekijken je aanvraag en nemen binnen één werkdag contact met je op. Heb je haast? Bel{" "}
          <a href={site.phoneHref}
      onClick={() => trackConversion("telefoon_klik")} className="font-semibold text-brand">
            {site.phone}
          </a>{" "}
          of stuur ons een WhatsApp-bericht.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Je aanvraag komt binnen bij{" "}
          <a href={site.emailHref} className="font-semibold text-brand break-all">
            {site.email}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="mt-6 rounded-md border border-border px-4 py-2 text-sm font-semibold text-secondary"
        >
          Nog een aanvraag doen
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {/* Honeypot: onzichtbaar voor bezoekers, alleen bots vullen dit in. */}
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="company_website">Website</label>
        <input id="company_website" name="company_website" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      {!compact && (
      <div className="grid gap-3 rounded-xl border border-brand/40 bg-brand/5 p-4">
        <div>
          <p className="text-sm font-semibold text-secondary">Direct contact</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Heeft u een korte vraag? Stel hem direct via e-mail of WhatsApp, dan bespreken we de details telefonisch.
            Weet u al meer over uw verhuizing? Vul dan de offerteaanvraag hieronder zo volledig mogelijk in — hoe meer
            wij weten, hoe beter wij met u mee kunnen denken.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={`${site.emailHref}?subject=${encodeURIComponent(
              "Korte vraag via de website",
            )}&body=${encodeURIComponent("Hoi Vakkundig,\n\nMijn vraag is:\n\n")}`}
            className="inline-flex h-11 items-center justify-center rounded-md bg-brand px-4 text-sm font-bold text-brand-foreground transition hover:bg-brand-dark"
          >
            Stel je vraag per e-mail
          </a>
          <a
            href={site.phoneHref}
      onClick={() => trackConversion("telefoon_klik")}
            className="inline-flex h-11 items-center justify-center rounded-md border border-brand px-4 text-sm font-bold text-brand transition hover:bg-brand/10"
          >
            Bel {site.phone}
          </a>
          <a
            href={site.whatsappHref}
      onClick={() => trackConversion("whatsapp_klik")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 items-center justify-center rounded-md border border-border px-4 text-sm font-semibold text-secondary transition hover:border-brand"
          >
            WhatsApp ons
          </a>
        </div>
      </div>
      )}

      {service ? (
        <p className="rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm font-semibold text-secondary">
          Aanvraag voor: {service}
        </p>
      ) : (
        <label className="grid gap-1">
          <span className="text-sm font-semibold text-secondary">
            Waar gaat je aanvraag over?<span className="text-brand"> *</span>
          </span>
          <select
            value={chosenService}
            onChange={(e) => setChosenService(e.target.value)}
            required
            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-secondary focus:border-brand focus:outline-none"
          >
            <option value="">Kies een dienst…</option>
            {services.map((s) => (
              <option key={s.slug} value={s.title}>
                {s.title}
              </option>
            ))}
          </select>
        </label>


      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Naam" name="name" required placeholder="Voor- en achternaam" />
        <Field label="Telefoonnummer" name="phone" type="tel" required placeholder="06 12345678" />
        <Field label="E-mailadres" name="email" type="email" required placeholder="naam@email.nl" />
        <Field label="Gewenste verhuisdatum" name="move_date" type="date" />
        <Field label="Verhuizen van (straat en plaats)" name="from_address" placeholder="Bijv. Dorpsstraat, Den Haag" />
        <Field label="Huisnummer (van)" name="from_number" placeholder="Bijv. 12A" />
        <Field label="Verhuizen naar (straat en plaats)" name="to_address" placeholder="Bijv. Kerkweg, Delft" />
        <Field label="Huisnummer (naar)" name="to_number" placeholder="Bijv. 45" />
      </div>

      {!compact && <InventoryCalculator value={inventory} onChange={setInventory} />}

      {compact && !showDetails ? (
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="justify-self-start text-sm font-semibold text-brand underline underline-offset-2"
        >
          + Extra details toevoegen (optioneel)
        </button>
      ) : (
      <>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-semibold text-secondary">Inpakservice gewenst?</legend>
        <div className="flex flex-wrap gap-2">
          {packingOptions.map((option) => {
            const active = packing === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setPacking(active ? "" : option)}
                aria-pressed={active}
                className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition ${
                  active
                    ? "border-brand bg-brand text-brand-foreground"
                    : "border-border bg-background text-secondary hover:border-brand"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {packing
            ? packingHelp[packing]
            : "Kies wat bij u past — wij pakken in, of u pakt zelf in en wij verzorgen de verhuizing."}
        </p>
      </fieldset>


      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-semibold text-secondary">Verhuislift nodig?</legend>
        <div className="flex flex-wrap gap-2">
          {liftOptions.map((option) => {
            const active = needsLift === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setNeedsLift(active ? "" : option)}
                aria-pressed={active}
                className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition ${
                  active
                    ? "border-brand bg-brand text-brand-foreground"
                    : "border-border bg-background text-secondary hover:border-brand"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="grid gap-2">
        <legend className="text-sm font-semibold text-secondary">Demonteren en monteren nodig?</legend>
        <p className="text-xs text-muted-foreground">Kies hieronder welke items wij voor je demonteren en weer opbouwen.</p>
        <div className="flex flex-wrap gap-2">
          {montageItems.map((option) => {
            const active = montage.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggleMontage(option)}
                aria-pressed={active}
                className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition ${
                  active
                    ? "border-brand bg-brand text-brand-foreground"
                    : "border-border bg-background text-secondary hover:border-brand"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="grid gap-2">
        <legend className="text-sm font-semibold text-secondary">Welke verdiepingen?</legend>
        <p className="text-xs text-muted-foreground">
          Kies alle verdiepingen waar je inboedel staat. Bijvoorbeeld begane grond + 1e etage + zolder.
        </p>
        <div className="flex flex-wrap gap-2">
          {floorOptions.map((option) => {
            const active = floors.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggleFloor(option)}
                aria-pressed={active}
                className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition ${
                  active
                    ? "border-brand bg-brand text-brand-foreground"
                    : "border-input text-secondary hover:border-brand"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
        <label className="mt-1 grid gap-1.5">
          <span className="text-sm font-semibold text-secondary">Hogere verdieping? Vul de verdieping in</span>
          <input
            name="floor_other"
            type="text"
            inputMode="numeric"
            placeholder="Bijv. 3e etage"
            className="h-11 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-brand"
          />
        </label>
      </fieldset>
      </>
      )}

      {!compact ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="text-sm font-semibold text-secondary">Type woning</span>
              <select
                name="home_type"
                className="h-11 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-brand"
                defaultValue=""
              >
                <option value="">Kies woningtype</option>
                {homeTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Oppervlakte of aantal kamers" name="surface" placeholder="Bijv. 85 m² / 3 kamers" />
          </div>
        </>
      ) : null}


      <label className="grid gap-1.5">
        <span className="text-sm font-semibold text-secondary">Vertel kort over je verhuizing</span>
        <textarea
          name="message"
          rows={compact ? 3 : 5}
          placeholder="Bijvoorbeeld: derde verdieping zonder lift, piano mee, en een schuur leeghalen."
          className="rounded-md border border-input bg-background px-3 py-2.5 text-sm outline-none focus-visible:border-brand"
        />
      </label>

      <button
        type="submit"
        disabled={loading}
        className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-brand px-6 font-bold text-brand-foreground transition hover:bg-brand-dark disabled:opacity-70"
      >
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        Offerte aanvragen
      </button>
      <p className="text-xs text-muted-foreground">
        Liever direct contact? Bel{" "}
        <a href={site.phoneHref}
      onClick={() => trackConversion("telefoon_klik")} className="font-semibold text-brand">
          {site.phone}
        </a>{" "}
        of stuur een WhatsApp-bericht naar {site.whatsapp}.
      </p>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-sm font-semibold text-secondary">
        {label}
        {required ? <span className="text-brand"> *</span> : null}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="h-11 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-brand"
      />
    </label>
  );
}
